import { FormEvent, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Flex, IconButton, Input, keyframes, useBreakpointValue } from '@chakra-ui/react';
import { useDebouncedState } from '@react-hookz/web';
import { CloseIcon, SearchIcon } from '@udacity/chakra-uds-icons';

import { updateCatalogSearchValue } from '~/features/catalog/utils/catalog-url-state-utils';
import { useI18n } from '~/hooks/use-i18n';

import {
  HEADER_SEARCH_ORIGIN,
  INLINE_SEARCH_ORIGIN,
  INLINE_SEARCH_PLACEHOLDER_TERMS,
  INLINE_SEARCH_SURFACE,
  URL_SEARCH_ORIGIN,
} from '../constants';
import { useTypewriterPlaceholder } from '../hooks/use-typewriter-placeholder';
import { setCatalogSearchDraft, useCatalogSearchDraft, useCatalogSearchOrigin } from '../utils/catalog-search-draft';

const INPUT_FONT_SIZE = { base: '1.125rem', md: '1.5rem' } as const;
const INPUT_LINE_HEIGHT = { base: '22px', md: '33px' } as const;
const SEARCH_ICON_SIZE = { base: '24px', md: '32px' } as const;

const CARET_HEIGHT = INPUT_FONT_SIZE;
const CARET_WIDTH = '2px';

const caretBlink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

function AnimatedCaret() {
  return (
    <Box
      animation={`${caretBlink} 1s step-end infinite`}
      bg='currentColor'
      flexShrink={0}
      h={CARET_HEIGHT}
      w={CARET_WIDTH}
    />
  );
}

function getUrlSearchValue(searchValue: string | string[] | undefined) {
  return typeof searchValue === 'string' ? searchValue : '';
}

export function CatalogInlineSearch() {
  const { isEnUS, t } = useI18n();
  const router = useRouter();
  const draft = useCatalogSearchDraft();
  const searchOrigin = useCatalogSearchOrigin();
  const urlSearchValue = getUrlSearchValue(router.query.searchValue);
  const shouldCommitDebouncedValue = useRef(false);
  const lastCommittedUrlValue = useRef<string | null>(null);
  const hasInitializedOriginCommit = useRef(false);
  const [debouncedDraft, setDebouncedDraft] = useDebouncedState(draft, 400);
  const [isFocused, setIsFocused] = useState(false);
  const [caretIndex, setCaretIndex] = useState(0);
  const hasValue = draft.trim().length > 0;
  const hasDraft = draft.length > 0;
  const placeholderDesktop = t('catalog.inlineSearchPlaceholder');
  const placeholderMobile = t('catalog.inlineSearchPlaceholderMobile');
  const isMobilePlaceholder = useBreakpointValue({ base: true, md: false }, { fallback: 'base' });
  const placeholder = isMobilePlaceholder ? placeholderMobile : placeholderDesktop;
  // Example terms are English-only, so keep the typewriter (and custom caret) off other locales.
  const canAnimateTypewriter = isEnUS;
  const { prefersReducedMotion, text: typewriterText } = useTypewriterPlaceholder({
    introText: placeholder,
    isPaused: !canAnimateTypewriter || isFocused || hasDraft,
    terms: INLINE_SEARCH_PLACEHOLDER_TERMS,
  });
  const showAnimatedCaret = canAnimateTypewriter && !prefersReducedMotion;
  const showTypewriterOverlay = showAnimatedCaret && !isFocused && !hasDraft;
  const showFocusCaret = showAnimatedCaret && isFocused;

  function syncCaretIndex(event: SyntheticEvent<HTMLInputElement>) {
    setCaretIndex(event.currentTarget.selectionStart ?? event.currentTarget.value.length);
  }

  useEffect(() => {
    setDebouncedDraft(draft);
  }, [draft, setDebouncedDraft]);

  useEffect(() => {
    // Skip the leftover module-store origin from a previous catalog visit. Arming on first
    // paint would debounce-commit that draft into a clean URL before hydration replaces it.
    if (!hasInitializedOriginCommit.current) {
      hasInitializedOriginCommit.current = true;
      return;
    }

    if (searchOrigin === HEADER_SEARCH_ORIGIN) {
      shouldCommitDebouncedValue.current = true;
    }
  }, [draft, searchOrigin]);

  useEffect(() => {
    if (urlSearchValue === lastCommittedUrlValue.current) {
      return;
    }

    lastCommittedUrlValue.current = urlSearchValue;
    setCatalogSearchDraft(urlSearchValue, URL_SEARCH_ORIGIN);
  }, [urlSearchValue]);

  useEffect(() => {
    if (!shouldCommitDebouncedValue.current) {
      return;
    }

    // The draft can be replaced outside this input (e.g. the no-results Clear Search button); a pending
    // debounced value is stale until it catches up, and committing it would restore the old query.
    if (debouncedDraft !== draft) {
      return;
    }

    if (debouncedDraft.trim() === urlSearchValue) {
      return;
    }

    lastCommittedUrlValue.current = debouncedDraft.trim();
    updateCatalogSearchValue({ router, searchValue: debouncedDraft });
  }, [debouncedDraft, draft, router, urlSearchValue]);

  function commitSearch(searchValue: string) {
    shouldCommitDebouncedValue.current = false;
    lastCommittedUrlValue.current = searchValue.trim();
    setCatalogSearchDraft(searchValue, INLINE_SEARCH_ORIGIN);
    updateCatalogSearchValue({ router, searchValue });
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    commitSearch(draft);
  }

  return (
    <Flex
      alignItems='center'
      as='form'
      borderBottom='1px solid'
      borderColor='grey.300'
      data-search-surface={INLINE_SEARCH_SURFACE}
      gap={4}
      py={4}
      role='search'
      onSubmit={handleSubmit}
    >
      <SearchIcon color='grey.900' flexShrink={0} h={SEARCH_ICON_SIZE} pointerEvents='none' w={SEARCH_ICON_SIZE} />
      <Box flex={1} minW={0} position='relative'>
        <Input
          _placeholder={{ color: 'grey.300' }}
          aria-label={placeholder}
          autoComplete='off'
          color='grey.900'
          fontSize={INPUT_FONT_SIZE}
          fontWeight={400}
          lineHeight={INPUT_LINE_HEIGHT}
          name='catalog-inline-search'
          pl={0}
          placeholder={showTypewriterOverlay ? '' : placeholder}
          pr={SEARCH_ICON_SIZE}
          type='search'
          value={draft}
          variant='unstyled'
          w='100%'
          sx={{
            caretColor: showAnimatedCaret ? 'transparent' : undefined,
            '&::-webkit-search-cancel-button': {
              display: 'none',
            },
          }}
          onBlur={() => setIsFocused(false)}
          onClick={syncCaretIndex}
          onKeyUp={syncCaretIndex}
          onSelect={syncCaretIndex}
          onChange={(event) => {
            shouldCommitDebouncedValue.current = true;
            setCatalogSearchDraft(event.target.value, INLINE_SEARCH_ORIGIN);
            syncCaretIndex(event);
          }}
          onFocus={(event) => {
            setIsFocused(true);
            syncCaretIndex(event);
          }}
        />
        {showTypewriterOverlay && (
          <Flex
            alignItems='center'
            color='grey.300'
            fontSize={INPUT_FONT_SIZE}
            fontWeight={400}
            inset={0}
            lineHeight={INPUT_LINE_HEIGHT}
            overflow='hidden'
            pointerEvents='none'
            position='absolute'
            whiteSpace='nowrap'
            aria-hidden
          >
            <Box as='span' overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>
              {typewriterText}
            </Box>
            <AnimatedCaret />
          </Flex>
        )}
        {showFocusCaret && (
          <Flex
            alignItems='center'
            color='grey.900'
            fontSize={INPUT_FONT_SIZE}
            fontWeight={400}
            inset={0}
            lineHeight={INPUT_LINE_HEIGHT}
            overflow='hidden'
            pointerEvents='none'
            position='absolute'
            whiteSpace='nowrap'
            aria-hidden
          >
            <Box as='span' overflow='hidden' visibility='hidden' whiteSpace='nowrap'>
              {draft.slice(0, caretIndex)}
            </Box>
            <AnimatedCaret />
          </Flex>
        )}
        {hasValue && (
          <IconButton
            aria-label={t('catalog.inlineSearchClear')}
            color='grey.900'
            h={SEARCH_ICON_SIZE}
            icon={<CloseIcon h={SEARCH_ICON_SIZE} w={SEARCH_ICON_SIZE} />}
            minW={SEARCH_ICON_SIZE}
            p={0}
            position='absolute'
            right={0}
            top='50%'
            transform='translateY(-50%)'
            type='button'
            variant='unstyled'
            w={SEARCH_ICON_SIZE}
            onClick={() => commitSearch('')}
          />
        )}
      </Box>
    </Flex>
  );
}
